<template>
    <zen-grid-body-row>
        <zen-grid-body-cell
            v-if="showViewMode"
            :hidable="false"
            :primary="true"
        >
            {{ value.display != '' ? value.display : '(empty)' }}
            <zen-grid-mobile-detail v-if="!jsonEditorEnabled">
                <zen-grid-mobile-detail-item label="JSON">
                    {{ formattedValue }}
                </zen-grid-mobile-detail-item>
            </zen-grid-mobile-detail>
        </zen-grid-body-cell>
        <zen-grid-body-cell
            v-if="showViewMode && !jsonEditorEnabled"
            :content="formattedValue"
        />
        <zen-grid-body-cell
            v-if="showViewMode && showButtons"
            alignment="right"
        >
            <zen-table-icon
                v-if="
                    showButtons &&
                    can(PERMISSIONS.FLAG_UPDATE, {
                        projectUlid,
                        environmentUlid,
                    })
                "
                @click="toggleUpdateModal"
                :srText="
                    'Update ' + (value != null ? value.display : '') + ' Value'
                "
                tooltipText="Update"
            >
                <zen-icon-update />
            </zen-table-icon>
            <zen-table-icon
                v-if="
                    showButtons &&
                    can(PERMISSIONS.FLAG_DELETE, {
                        projectUlid,
                        environmentUlid,
                    })
                "
                @click="toggleRemoveModal"
                :srText="
                    'Remove ' + (value != null ? value.display : '') + ' Value'
                "
                tooltipText="Remove"
            >
                <zen-icon-remove />
            </zen-table-icon>
        </zen-grid-body-cell>
        <td
            v-if="showEditMode"
            :colspan="jsonEditorEnabled ? 2 : 3"
            class="px-3 py-4 text-sm lg:text-base text-taupe-500 lg:table-cell"
        >
            <div
                id="tabs-1-panel-1"
                class="-m-0.5 rounded-lg p-0.5"
                aria-labelledby="tabs-1-tab-1"
                role="tabpanel"
            >
                <label for="comment" class="sr-only">Comment</label>
                <div>
                    <textarea
                        v-if="usesPlainTextEditor"
                        v-model="modals.update.display"
                        rows="3"
                        name="display"
                        id="display"
                        class="block w-full rounded-md border-0 py-1.5 text-taupe-800 shadow-xs ring-1 ring-inset ring-taupe-300 placeholder:text-taupe-500 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6"
                        placeholder="Enter a string..."
                    />
                    <zen-json-editor
                        v-if="jsonEditorEnabled"
                        id="display"
                        v-model="modals.update.display"
                        @update:valid="modals.update.jsonValid = $event"
                    />
                </div>
            </div>
            <div
                class="mt-2 flex space-x-2"
                :class="isNumber ? 'justify-start' : 'justify-end'"
            >
                <input
                    v-if="isNumber"
                    type="text"
                    v-model="modals.update.display"
                    name="display"
                    id="display"
                    class="block w-1/2 rounded-md border-0 py-1.5 text-taupe-800 shadow-xs ring-1 ring-inset ring-taupe-300 placeholder:text-taupe-500 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6"
                    placeholder="Enter a number..."
                />
                <zen-button
                    @click="confirmUpdate"
                    type="submit"
                    variant="primary"
                    :disabled="jsonEditorEnabled && !modals.update.jsonValid"
                >
                    {{ isCreate ? 'Create' : 'Update' }}
                </zen-button>
                <zen-button @click="toggleUpdateModal" variant="tertiary">
                    Cancel
                </zen-button>
            </div>
        </td>
        <remove-flag-value-modal
            :is-open="modals.remove.showing"
            @close="toggleRemoveModal"
            @confirm="confirmRemove"
        />
    </zen-grid-body-row>
</template>

<script>
/* Services */
import FlagValueService from '@/services/flag-value.service'
import RemoveFlagValueModal from '@/screens/trusted/project/flags/detail/modals/RemoveFlagValueModal.vue'

import { errorMessage } from '@/utils/notifications'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'

export default {
    name: 'ValueTableRow',
    components: { RemoveFlagValueModal },
    setup() {
        const { can } = usePermissions()
        return { can, PERMISSIONS }
    },
    computed: {
        isCreate() {
            return this.value === null
        },
        isEdit() {
            return !this.isCreate
        },
        isNumber() {
            return this.type == 'number'
        },
        isString() {
            return this.type == 'string'
        },
        isJson() {
            return this.type == 'json'
        },
        // The rich CodeMirror editor (and the single-column Values-tab
        // layout it enables) is gated behind the `allow-json-flags`
        // zenmanage feature flag so the enhanced experience only ships to
        // accounts in the json-flags rollout. Everyone else keeps the
        // legacy plain-textarea/two-column display for a json value.
        jsonEditorEnabled() {
            return this.isJson && this.jsonUiEnabled
        },
        usesPlainTextEditor() {
            return this.isString || (this.isJson && !this.jsonUiEnabled)
        },
        showButtons() {
            return this.type != 'boolean' && !this.readOnly
        },
        showEditMode() {
            return this.modals.update.showing || this.value === null
        },
        showViewMode() {
            return !this.showEditMode
        },
        formattedValue() {
            const rawValue = this.value?.value

            if (rawValue === null || rawValue === undefined) {
                return 'null'
            }

            if (typeof rawValue === 'object') {
                try {
                    return JSON.stringify(rawValue)
                } catch {
                    return String(rawValue)
                }
            }

            return String(rawValue)
        },
    },
    data() {
        return {
            modals: {
                remove: {
                    showing: false,
                },
                update: {
                    display: null,
                    showing: false,
                    jsonValid: true,
                },
            },
        }
    },
    methods: {
        confirmRemove() {
            FlagValueService.remove(
                this.$route.params.projectKey,
                this.$route.params.key,
                this.value.ulid
            )
                .then((_response) => {
                    this.toggleRemoveModal()
                    this.$emit('removed', this.value)
                })
                .catch((error) => {
                    errorMessage(error)
                })
        },
        confirmUpdate() {
            if (this.jsonEditorEnabled && !this.modals.update.jsonValid) {
                return
            }

            if (this.value !== null) {
                FlagValueService.update(
                    this.$route.params.projectKey,
                    this.$route.params.key,
                    this.value.ulid,
                    { display: this.modals.update.display }
                )
                    .then((response) => {
                        this.toggleUpdateModal()
                        this.$emit('updated', response.data)
                    })
                    .catch((error) => {
                        errorMessage(error)
                    })
            } else {
                FlagValueService.create(
                    this.$route.params.projectKey,
                    this.$route.params.key,
                    { display: this.modals.update.display }
                )
                    .then((response) => {
                        this.toggleUpdateModal()
                        this.$emit('created', response.data)
                    })
                    .catch((error) => {
                        errorMessage(error)
                    })
            }
        },
        toggleRemoveModal() {
            this.modals.remove.showing = !this.modals.remove.showing
        },
        toggleUpdateModal() {
            if (this.value === null) {
                this.$emit('cancelled')
                return
            }

            this.modals.update.showing = !this.modals.update.showing
            if (!this.modals.update.showing) {
                this.modals.update.display = null
                this.modals.update.jsonValid = true
            } else {
                this.modals.update.display = this.value.display
            }
        },
    },
    props: {
        value: {
            default: null,
            type: Object,
        },
        type: {
            required: true,
            type: String,
        },
        jsonUiEnabled: {
            default: false,
            type: Boolean,
        },
        projectUlid: {
            default: null,
            type: String,
        },
        environmentUlid: {
            default: null,
            type: String,
        },
        // Hides the update and remove controls, e.g. while a temporary swap runs on the environment.
        readOnly: {
            default: false,
            type: Boolean,
        },
    },
}
</script>
